import React from 'react'
import { Switch } from '@/features/shadcn/ui/switch'

export type TaskDeliveryFieldsProps = {
  notifyOnComplete: boolean
  onNotifyOnCompleteChange: (v: boolean) => void
  selectedNotifyEmails: string[]
  onToggleNotifyEmail: (email: string) => void
  userEmail: string
  teamMembers?: Array<{ shared_with?: string; email?: string }>
  teamMembersLoading?: boolean
  /** When set, show Slack toggle that preserves existing channel delivery */
  slackDelivery?: { enabled: boolean; channelLabel?: string } | null
  onSlackDeliveryChange?: (enabled: boolean) => void
}

function teamMemberEmail(account: { shared_with?: string; email?: string }): string {
  return String(account.shared_with || account.email || '')
    .trim()
    .toLowerCase()
}

export default function TaskDeliveryFields({
  notifyOnComplete,
  onNotifyOnCompleteChange,
  selectedNotifyEmails,
  onToggleNotifyEmail,
  userEmail,
  teamMembers,
  teamMembersLoading = false,
  slackDelivery,
  onSlackDeliveryChange,
}: TaskDeliveryFieldsProps) {
  const showSlack = slackDelivery != null

  return (
    <div className="tw-space-y-4">
      <div>
        <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0 tw-flex-1">
            <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-800">
              Notify by email when complete
            </p>
            <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-slate-500">
              Choose team members to alert when each run finishes
            </p>
          </div>
          <Switch
            checked={notifyOnComplete}
            onCheckedChange={(checked) => onNotifyOnCompleteChange(!!checked)}
            aria-label="Notify by email when each run completes"
            className="tw-shrink-0 data-[state=checked]:tw-bg-blue-600 data-[state=unchecked]:tw-bg-slate-200"
          />
        </div>
        {notifyOnComplete && (
          <div className="tw-mt-3 tw-space-y-1.5">
            <p className="tw-m-0 tw-text-xs tw-font-medium tw-text-slate-600">Team members</p>
            {teamMembers && teamMembers.length > 0 ? (
              teamMembers.map((account) => {
                const email = teamMemberEmail(account)
                if (!email) return null
                const selected = selectedNotifyEmails.includes(email)
                return (
                  <label
                    key={email}
                    className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm tw-text-slate-700"
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleNotifyEmail(email)}
                      className="tw-rounded tw-border-slate-300"
                    />
                    <span className={selected ? 'tw-font-medium' : 'tw-font-normal tw-opacity-70'}>
                      {email}
                    </span>
                  </label>
                )
              })
            ) : teamMembersLoading ? (
              <p className="tw-m-0 tw-text-xs tw-text-slate-500">Loading team members…</p>
            ) : (
              <p className="tw-m-0 tw-text-xs tw-text-slate-500">
                {userEmail
                  ? `No team list yet — will notify ${userEmail}`
                  : 'No team members available. Add teammates in Settings.'}
              </p>
            )}
            {selectedNotifyEmails.length === 0 && (
              <p className="tw-m-0 tw-text-xs tw-text-amber-600">
                Select at least one email, or we&apos;ll use your account email.
              </p>
            )}
          </div>
        )}
        <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-slate-500">
          We&apos;ll send a short email when each run finishes (success or failure). No attachments.
        </p>
      </div>

      {showSlack && (
        <div className="tw-pt-3 tw-border-t tw-border-slate-100">
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
            <div className="tw-min-w-0 tw-flex-1">
              <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-slate-800">
                Notify in Slack when complete
              </p>
              <p className="tw-m-0 tw-mt-0.5 tw-text-xs tw-text-slate-500">
                {slackDelivery.channelLabel
                  ? `Posts to ${slackDelivery.channelLabel}`
                  : "Uses this task's existing Slack channel"}
              </p>
            </div>
            <Switch
              checked={!!slackDelivery.enabled}
              onCheckedChange={(checked) => onSlackDeliveryChange?.(!!checked)}
              aria-label="Notify in Slack when each run completes"
              className="tw-shrink-0 data-[state=checked]:tw-bg-blue-600 data-[state=unchecked]:tw-bg-slate-200"
            />
          </div>
          <p className="tw-m-0 tw-mt-2 tw-text-xs tw-text-slate-500">
            Turning this off stops Slack posts for this task. Re-enable before saving to keep the linked channel.
          </p>
        </div>
      )}
    </div>
  )
}
